import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Truck, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra',
    'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Jammu and Kashmir', 'Ladakh',
    'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep', 'Puducherry', 'Andaman and Nicobar Islands'
];

export default function Shipping() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({ state: '', base_price: 50, base_weight_kg: 0.5, increment_per_0_5kg: 25, active: true });
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/shipping/rates'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ state: '', base_price: 50, base_weight_kg: 0.5, increment_per_0_5kg: 25, active: true }); setDialog(true); };
    const openEdit = (r) => { setEditing(r); setForm({ ...r }); setDialog(true); };

    const save = async () => {
        try { await api.post('/shipping/rates', form); toast.success('Saved'); setDialog(false); load(); }
        catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };
    const doDelete = async (state) => {
        if (!window.confirm(`Delete rate for ${state}?`)) return;
        try { await api.delete(`/shipping/rates/${state}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    const availableStates = INDIAN_STATES.filter(s => editing?.state === s || !items.find(x => x.state === s));

    return (
        <AppShell pageTitle="Shipping Rates" pageActions={
            <Button onClick={openNew} data-testid="add-shipping-rate-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}><Plus className="h-4 w-4 mr-2" />Add Rate</Button>
        }>
            <div className="text-xs text-neutral-600 mb-3">Base rate applies to shipments ≤ 0.5 kg. Additional 0.5 kg slabs are billed at the increment rate.</div>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Truck} title="No shipping rates" description="Add state-wise rates to enable checkout." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Add Rate</Button>} />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="shipping-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">State</th>
                                <th className="text-right font-medium">Base Price</th>
                                <th className="text-right font-medium">Base Weight (kg)</th>
                                <th className="text-right font-medium">+ per 0.5kg</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(r => (
                                <tr key={r.state} className="border-b hover:bg-neutral-50" data-testid={`shipping-row-${r.state}`}>
                                    <td className="p-3 font-medium">{r.state}</td>
                                    <td className="text-right tabular-nums">₹{r.base_price}</td>
                                    <td className="text-right tabular-nums">{r.base_weight_kg}</td>
                                    <td className="text-right tabular-nums">₹{r.increment_per_0_5kg}</td>
                                    <td>{r.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(r.state)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? 'Edit Rate' : 'Add State Rate'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>State</Label>
                            <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} disabled={!!editing} data-testid="shipping-state-select">
                                <option value="">Select state</option>
                                {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label>Base Price (₹)</Label><Input type="number" value={form.base_price} onChange={e => setForm({ ...form, base_price: parseFloat(e.target.value) || 0 })} data-testid="shipping-base-price-input" /></div>
                            <div><Label>Base Weight (kg)</Label><Input type="number" step="0.1" value={form.base_weight_kg} onChange={e => setForm({ ...form, base_weight_kg: parseFloat(e.target.value) || 0.5 })} /></div>
                            <div><Label>Increment (₹ / 0.5kg)</Label><Input type="number" value={form.increment_per_0_5kg} onChange={e => setForm({ ...form, increment_per_0_5kg: parseFloat(e.target.value) || 0 })} data-testid="shipping-increment-input" /></div>
                        </div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-shipping-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
